export const countriesWithRegions = [
  'AU',
  'BR',
  'CA',
  'CN',
  'IE',
  'JP',
  'KR',
  'MX',
  'MY',
  'RU',
  'TR',
  'US',
] as const;

// Matches Stripe API: https://stripe.com/docs/js/tokens_sources/create_token?type=cardElement.
// The `address_` has been removed from the strings for readability purposes. There's an util
// `mapBillingAddressToStripe` to create the object appending the `address_` prefix.
export enum EBillingAddressFields {
  CITY = 'city',
  COUNTRY = 'country',
  LINE_1 = 'line1',
  LINE_2 = 'line2',
  ZIP_CODE = 'zip',
  STATE = 'state',
}

export enum EBillingAddressFieldVariants {
  PREFECTURE = 'prefecture',
  PROVINCE = 'province',
  POSTAL_CODE = 'postal_code',
}

export type TDynamicBillingAddressFields = Exclude<EBillingAddressFields, 'country'>;
export interface IDynamicBillingAddressField {
  sort: number;
  variant?: EBillingAddressFieldVariants;
}

export type TBillingAddress = Partial<
  Record<TDynamicBillingAddressFields, IDynamicBillingAddressField>
>;

export type TBillingForm = Record<EBillingAddressFields, string>;

export const DEFAULT_ADDRESS: TBillingAddress = {
  line1: {
    sort: 0,
  },
  line2: {
    sort: 1,
  },
  city: {
    sort: 2,
  },
  zip: {
    sort: 4,
    variant: EBillingAddressFieldVariants.POSTAL_CODE,
  },
};

const ADDRESS_WITHOUT_STATE = {
  line1: {
    sort: 0,
  },
  line2: {
    sort: 1,
  },
  zip: {
    sort: 2,
    variant: EBillingAddressFieldVariants.POSTAL_CODE,
  },
  city: {
    sort: 3,
  },
};

const ADDRESS_WITH_STATE_AND_POSTAL_CODE = {
  line1: {
    sort: 0,
  },
  line2: {
    sort: 1,
  },
  city: {
    sort: 3,
  },
  state: {
    sort: 4,
  },
  zip: {
    sort: 5,
    variant: EBillingAddressFieldVariants.POSTAL_CODE,
  },
};

const ADDRESS_WITH_PROVINCE_AND_POSTAL_CODE = {
  line1: {
    sort: 0,
  },
  line2: {
    sort: 1,
  },
  city: {
    sort: 2,
  },
  state: {
    sort: 3,
    variant: EBillingAddressFieldVariants.PROVINCE,
  },
  zip: {
    sort: 4,
    variant: EBillingAddressFieldVariants.POSTAL_CODE,
  },
};

const ADDRESS_WITHOUT_STATE_AND_CITY_FIRST = {
  line1: {
    sort: 0,
  },
  line2: {
    sort: 1,
  },
  city: {
    sort: 2,
  },
  zip: {
    sort: 3,
    variant: EBillingAddressFieldVariants.POSTAL_CODE,
  },
};

export const addressByCountry: Record<string, TBillingAddress> = {
  AT: ADDRESS_WITHOUT_STATE,
  AU: {
    line1: {
      sort: 0,
    },
    line2: {
      sort: 1,
    },
    city: {
      sort: 2,
    },
    state: {
      sort: 3,
    },
    zip: {
      sort: 4,
      variant: EBillingAddressFieldVariants.POSTAL_CODE,
    },
  },
  BE: ADDRESS_WITHOUT_STATE,
  BR: ADDRESS_WITH_STATE_AND_POSTAL_CODE,
  BG: ADDRESS_WITHOUT_STATE,
  CA: ADDRESS_WITH_PROVINCE_AND_POSTAL_CODE,
  CN: ADDRESS_WITH_PROVINCE_AND_POSTAL_CODE,
  CZ: ADDRESS_WITHOUT_STATE,
  DE: ADDRESS_WITHOUT_STATE_AND_CITY_FIRST,
  DK: ADDRESS_WITHOUT_STATE,
  FI: ADDRESS_WITHOUT_STATE,
  FR: ADDRESS_WITHOUT_STATE_AND_CITY_FIRST,
  GB: ADDRESS_WITHOUT_STATE_AND_CITY_FIRST,
  GR: ADDRESS_WITHOUT_STATE,
  HU: ADDRESS_WITHOUT_STATE,
  IE: ADDRESS_WITH_PROVINCE_AND_POSTAL_CODE,
  IT: ADDRESS_WITHOUT_STATE_AND_CITY_FIRST,
  JP: {
    line1: {
      sort: 0,
    },
    line2: {
      sort: 1,
    },
    zip: {
      sort: 2,
      variant: EBillingAddressFieldVariants.POSTAL_CODE,
    },
    city: {
      sort: 3,
    },
    state: {
      sort: 4,
      variant: EBillingAddressFieldVariants.PREFECTURE,
    },
  },
  KR: ADDRESS_WITH_PROVINCE_AND_POSTAL_CODE,
  MX: ADDRESS_WITH_STATE_AND_POSTAL_CODE,
  MY: ADDRESS_WITH_STATE_AND_POSTAL_CODE,
  NL: ADDRESS_WITHOUT_STATE,
  NO: ADDRESS_WITHOUT_STATE,
  NZ: ADDRESS_WITHOUT_STATE_AND_CITY_FIRST,
  PL: ADDRESS_WITHOUT_STATE,
  PT: ADDRESS_WITHOUT_STATE,
  HR: ADDRESS_WITHOUT_STATE,
  RS: ADDRESS_WITHOUT_STATE,
  SI: ADDRESS_WITHOUT_STATE,
  RO: ADDRESS_WITHOUT_STATE,
  RU: ADDRESS_WITH_PROVINCE_AND_POSTAL_CODE,
  ES: ADDRESS_WITHOUT_STATE,
  SE: ADDRESS_WITHOUT_STATE,
  CH: ADDRESS_WITHOUT_STATE,
  TR: {
    line1: {
      sort: 0,
    },
    line2: {
      sort: 1,
    },
    zip: {
      sort: 3,
      variant: EBillingAddressFieldVariants.POSTAL_CODE,
    },
    state: {
      sort: 4,
      variant: EBillingAddressFieldVariants.PROVINCE,
    },
    city: {
      sort: 5,
    },
  },
  US: {
    line1: {
      sort: 0,
    },
    line2: {
      sort: 1,
    },
    city: {
      sort: 2,
    },
    state: {
      sort: 3,
    },
    zip: {
      sort: 4,
    },
  },
};
